// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/AskV2ThreadFindings.test.tsx
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/Thread.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IN_PROGRESS_FINDINGS_SHOWN,
  inlineFindingAria,
} from "../rail/collectRailContent";
import { type AskV2RailFinding } from "../rail/types";

/**
 * Same footprint as the static tool-result row in `AgentSteps` (`ToolResultStep`
 * when it is not an artifact link): `TOOL_CHIP_CLASS` plus the done check and
 * name/secondary labels. ask-v2 cannot import `routes/ask-puntt` (import wall),
 * so the classes are copied rather than a new card.
 */
const TOOL_CALL_ROW_CLASS =
  "flex w-full min-w-0 items-center gap-2 rounded-lg bg-transparent px-[11px] py-[7px]";

/**
 * Activity-row chrome from `STATUS_PANEL_TOGGLE_MUTED_CLASS` plus its hover.
 * Copied: ask-v2 cannot import `routes/ask-puntt`.
 */
const FINDINGS_TOGGLE_CLASS =
  "flex w-full items-center gap-2 bg-transparent px-2.5 py-1.5 text-left outline-none hover:bg-puntt-neutral-gray-3 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7";

/**
 * Early verified findings, collapsed in the thread.
 *
 * The rows are what warhol has verified so far. They stay behind one activity
 * row until she opens it, and the open list is read-only: heart and delete
 * belong on the finished review cards.
 */
export function AskV2ThreadFindings({
  findings,
}: {
  findings: AskV2RailFinding[];
}) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const visible =
    // Stryker disable next-line ConditionalExpression,EqualityOperator: only the inner `findings.length <= IN_PROGRESS_FINDINGS_SHOWN` ConditionalExpression replacement is `-> false` (its `||` parent suppresses `-> true`), and when showAll is false the original already sends a list of five or fewer to `findings`, which is the same rows `findings.slice(0, 5)` returns, so no input renders differently; EqualityOperator `-> <` differs from `<=` only at exactly five without showAll, where `slice(0, 5)` is again the whole list. The other replacements this line silences are NOT equivalent: the whole `showAll || ...` check `-> true` and `-> false`, and EqualityOperator `-> >`, each change which rows render past five and are all killed by "hides in-progress comments past five until show more is opened".
    showAll || findings.length <= IN_PROGRESS_FINDINGS_SHOWN
      ? findings
      : findings.slice(0, IN_PROGRESS_FINDINGS_SHOWN);

  return (
    <div className="my-3" data-testid="ask-v2-thread-found-so-far">
      <button
        type="button"
        className={FINDINGS_TOGGLE_CLASS}
        data-testid="ask-v2-thread-found-so-far-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {/* why: this row is still finding issues, so it uses the same spinner
            as an in-progress step, not the done check the finished rows use. */}
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          aria-hidden
        />
        <Text
          as="span"
          size="1"
          weight="regular"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {t("askV2.rail.findingPotentialIssues")}
        </Text>
        {open ? (
          <CaretDown
            size={14}
            className="ml-auto shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="ml-auto shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>
      {open ? (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {visible.map((finding) => {
            return (
              <div
                key={finding.intentId}
                className={TOOL_CALL_ROW_CLASS}
                data-testid={`ask-v2-thread-finding-${finding.intentId}`}
                aria-label={inlineFindingAria(
                  finding.status,
                  finding.description,
                )}
              >
                <CheckCircle
                  size={14}
                  weight="fill"
                  className="shrink-0 text-puntt-neutral-grayA-11"
                  aria-hidden
                />
                {finding.status ? (
                  <Text
                    as="span"
                    size="1"
                    weight="regular"
                    title={finding.status}
                    className="min-w-0 max-w-[45%] shrink-0 truncate whitespace-nowrap text-puntt-neutral-gray-12"
                    data-testid={`ask-v2-thread-finding-status-${finding.intentId}`}
                  >
                    {finding.status}
                  </Text>
                ) : null}
                <Text
                  as="span"
                  size="1"
                  title={finding.description}
                  className="min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-9"
                >
                  {finding.description}
                </Text>
              </div>
            );
          })}
          {findings.length > IN_PROGRESS_FINDINGS_SHOWN ? (
            <button
              type="button"
              className="mt-1 cursor-pointer px-[11px] text-left text-[12px] font-medium text-puntt-neutral-gray-11 hover:text-puntt-neutral-gray-12"
              data-testid="ask-v2-thread-findings-show-more"
              aria-expanded={showAll}
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll ? t("showLess") : t("showMore")}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
